import { useState } from "react";
import ReactGA from "react-ga4";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useLogout, useCurrentUser } from "utils/hooks";
import {
  updateFilesAction,
  updatePublicFilesAction,
  addGroupFilesAction,
} from "store/actions/files";

type Props = {
  fileIds: Array<string> | [];
  patientId?: string;
  groupId?: string;
};

const useAssignFiles = ({}) => {
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { user } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [assigned, setAssigned] = useState(false);

  const formatIds = (ids: Array<string> | []) => ids.map((id) => `"${id}"`);

  const assignFiles = ({ fileIds, patientId, groupId }: Props) => {
    setAssigned(false);
    setLoading(true);

    return new Promise((resolve: any) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                assignFilesToPatientOrGroup(
                  fileIds: [${formatIds(fileIds ? fileIds : [])}], 
                  patientId: ${patientId ? `"${patientId}"` : `""`},
                  groupId: ${groupId ? `"${groupId}"` : `""`}
                ) {
                    fileUrl
                    title
                    description
                    order
                    uuid
                    addedBy
                    sharedWith {
                        value
                        label
                    }
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "share_files",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                setLoading(false);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                setLoading(false);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              resolve();
              setLoading(false);
              return true;
            } else {
              toast("Files added!");
              resolve();
              setLoading(false);
              const data = resData.data.assignFilesToPatientOrGroup;
              ReactGA.event("assign_files_success", {
                user_id: user?.id,
                files: data,
              });
              dispatch(updateFilesAction(data));
              dispatch(updatePublicFilesAction(data));
              if (groupId) dispatch(addGroupFilesAction(data));
              setAssigned(true);
              return true;
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    assignFiles,
    loading,
    assigned,
  };
};

export default useAssignFiles;
